import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Button,
  Modal,
  Input,
  Typography,
  Divider,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDrawPolygon } from '@fortawesome/free-solid-svg-icons';
import {
  MIN_RADIUS,
  MAX_RADIUS,
  NUMBER_OF_POINTS_FOR_CIRCLE,
  MIN_MINUTES,
  MAX_MINUTES,
  MIN_TOLERANCE,
  MAX_TOLERANCE,
  isValidRadius,
  isValidMinutes,
} from '../../../../constants/customArea';
import { mapboxApiClient } from '../../../../infrastructure/mapbox/client';
import { useInstructionsVisibility } from '../../../../hooks/SimViewer/useInstructionsVisibility';
import { useCustomAreaEdition } from '../../../../hooks/SimViewer/useCustomAreaEdition';
import { useCustomAreas } from '../../../../hooks/SimViewer/useCustomAreas';
import type { PolygonType } from '../../../../types/domain';
import { createCircle, simplifyPolygon } from '../../../../utils/geospatial';

type Props = {
  customAreaKey: string | null;
  customAreaType: PolygonType | null;
  isOpen: boolean;
};

const CustomAreaEditModal = ({
  customAreaKey,
  customAreaType,
  isOpen,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { instructionsVisibility, toggleInstructionsVisibility } =
    useInstructionsVisibility();
  const {
    editCustomArea,
    undoCustomArea,
    resetCustomArea,
    canUndo,
    endCustomAreaEdition,
  } = useCustomAreaEdition();
  const { getCustomAreaByKey } = useCustomAreas();

  const editingCustomArea = getCustomAreaByKey(customAreaKey || '');

  const [newRadius, setNewRadius] = useState<string>(
    String(editingCustomArea?.radius || '')
  );
  const [newMinutes, setNewMinutes] = useState<string>(
    String(editingCustomArea?.minutes || '')
  );

  const changeRadius = (radius: number): void => {
    if (!editingCustomArea) {
      return;
    }

    const newCircle = createCircle(editingCustomArea.center, radius, {
      steps: NUMBER_OF_POINTS_FOR_CIRCLE,
      units: 'kilometers',
    });

    editCustomArea({
      radius: radius,
      geometry: {
        coordinates: newCircle.coordinates[0],
      },
    });
  };
  const changeMinutes = async (minutes: number): Promise<void> => {
    if (!editingCustomArea) {
      return;
    }

    const geometry = await mapboxApiClient.getIsochrone(
      editingCustomArea.center,
      minutes,
      'walking'
    );
    if (!geometry) {
      return;
    }

    // 分数が大きいほど大雑把な形状になるように、トレランスの値を線形に変化させる
    const tolerance =
      ((MAX_TOLERANCE - MIN_TOLERANCE) / (MAX_MINUTES - MIN_MINUTES)) *
        (minutes - MIN_MINUTES) +
      MIN_TOLERANCE;
    const simplifiedGeometry = simplifyPolygon(geometry, tolerance);

    editCustomArea({
      minutes: minutes,
      geometry: {
        coordinates: simplifiedGeometry.coordinates[0],
      },
    });
  };
  const convertToPolygon = (): void => {
    if (!editingCustomArea) {
      return;
    }

    editCustomArea({
      type: 'polygon',
      radius: 0,
      minutes: 0,
    });
  };
  const cancelEditing = useCallback((): void => {
    if (!editingCustomArea) {
      return;
    }
    resetCustomArea();
    endCustomAreaEdition();
  }, [editingCustomArea, endCustomAreaEdition, resetCustomArea]);
  const confirmEditing = useCallback((): void => {
    endCustomAreaEdition();
  }, [endCustomAreaEdition]);

  useEffect((): void | (() => void) => {
    if (isOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          confirmEditing();
        } else if (event.key === 'Escape') {
          cancelEditing();
        } else if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
          undoCustomArea();
          event.preventDefault();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return (): void => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, confirmEditing, cancelEditing, undoCustomArea]);

  const instructions = editingCustomArea
    ? t(`message.${editingCustomArea.type}CustomAreaEditingInstructions`, {
        returnObjects: true,
      })
    : [];

  return (
    <Modal
      open={isOpen}
      destroyOnClose
      closable={false}
      mask={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width="fit-content"
      style={{
        top: instructionsVisibility.editCustomArea ? '75vh' : '80vh',
        padding: 0,
        minWidth: 400,
      }}
      wrapProps={{ style: { pointerEvents: 'none' } }}
      bodyStyle={{ paddingInline: 16, paddingBlock: 8, borderRadius: 8 }}
    >
      {editingCustomArea && (
        <>
          <RowContainer>
            <Typography.Text
              style={{ flexShrink: 0, flexGrow: 1, marginRight: 16 }}
            >
              {t('editingCustomArea', {
                type: t(
                  editingCustomArea.type === 'walking'
                    ? 'walkingDistance'
                    : editingCustomArea.type
                ),
              })}
            </Typography.Text>
            <Button
              type="default"
              shape="round"
              icon={<DeleteOutlined />}
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
              onClick={cancelEditing}
            >
              {t('discardChanges')}
            </Button>
            {customAreaType === 'polygon' ? (
              <Button
                type="default"
                shape="round"
                icon={<ArrowLeftOutlined />}
                disabled={!canUndo}
                onClick={undoCustomArea}
              >
                {t('undo')}
              </Button>
            ) : ['circle', 'walking'].includes(customAreaType || '') ? (
              <Button
                type="default"
                shape="round"
                icon={
                  <FontAwesomeIcon className="anticon" icon={faDrawPolygon} />
                }
                onClick={convertToPolygon}
              >
                {t('convertToPolygon')}
              </Button>
            ) : null}
            {customAreaType === 'polygon' ? null : customAreaType ===
              'circle' ? (
              <RowContainer>
                {t('radius')}
                <Tooltip
                  open={isOpen && !isValidRadius(newRadius)}
                  title={t('message.invalidRadius', {
                    min: MIN_RADIUS,
                    max: MAX_RADIUS,
                  })}
                >
                  <Input
                    type="number"
                    name="radius"
                    value={newRadius}
                    min={MIN_RADIUS}
                    max={MAX_RADIUS}
                    step="0.1"
                    autoFocus
                    style={{ width: 80, padding: '4px 8px' }}
                    status={isValidRadius(newRadius) ? undefined : 'error'}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      const value = event.currentTarget.value.trim();

                      setNewRadius(value);
                      if (
                        !Number.isNaN(Number(value)) &&
                        Number(value) >= MIN_RADIUS &&
                        Number(value) <= MAX_RADIUS
                      ) {
                        changeRadius(Number(value));
                      }
                    }}
                  />
                </Tooltip>
                km
              </RowContainer>
            ) : customAreaType === 'walking' ? (
              <RowContainer>
                {t('travelMode.walk')}
                <Tooltip
                  open={isOpen && !isValidMinutes(newMinutes)}
                  title={t('message.invalidWalkingMins', {
                    min: MIN_MINUTES,
                    max: MAX_MINUTES,
                  })}
                >
                  <Input
                    type="number"
                    name="minutes"
                    value={newMinutes}
                    min={MIN_MINUTES}
                    max={MAX_MINUTES}
                    step="1"
                    autoFocus
                    style={{ width: 60, padding: '4px 8px' }}
                    status={isValidMinutes(newMinutes) ? undefined : 'error'}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      const value = event.currentTarget.value.trim();

                      setNewMinutes(value);

                      if (
                        !Number.isNaN(Number(value)) &&
                        Number(value) >= MIN_MINUTES &&
                        Number(value) <= MAX_MINUTES
                      ) {
                        changeMinutes(Number(value));
                      }
                    }}
                  />
                </Tooltip>
                {t('unit.mins')}
              </RowContainer>
            ) : null}
            <Button
              type="primary"
              shape="round"
              icon={<CheckOutlined />}
              disabled={
                (customAreaType === 'circle' && !isValidRadius(newRadius)) ||
                (customAreaType === 'walking' && !isValidMinutes(newMinutes))
              }
              onClick={confirmEditing}
            >
              {t('actions.confirm')}
            </Button>
          </RowContainer>
          {instructionsVisibility.editCustomArea && (
            <ColumnContainer>
              <Divider
                type="horizontal"
                style={{
                  margin: '4px 0',
                  borderColor: 'var(--primary-color)',
                  opacity: 0.3,
                }}
              />
              <RowContainer>
                <Label>{t('instructions')}</Label>
                <StyledCheckbox
                  defaultChecked={false}
                  onChange={(): void => {
                    toggleInstructionsVisibility('editCustomArea');
                  }}
                >
                  {t('dontShowAgain')}
                </StyledCheckbox>
              </RowContainer>
              <BulletList>
                {instructions.map((line: string, index: number): ReactNode => {
                  return <ListItem key={index}>{line}</ListItem>;
                })}
              </BulletList>
            </ColumnContainer>
          )}
        </>
      )}
    </Modal>
  );
};

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 8px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const Label = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: auto;
  font-size: 12px;
  line-height: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;

  .ant-checkbox {
    top: 0;
  }

  .ant-checkbox + span {
    padding: 0;
    margin-left: 4px;
  }
`;

const BulletList = styled.ul`
  list-style-type: none;
  font-size: 14px;
  padding: 0;
  margin: 0 0 0 4px;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 14px;
  margin-top: 4px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: var(--primary-color);
    border-radius: 50%;
    position: absolute;
    top: 8px;
    left: 0;
  }
`;

export { CustomAreaEditModal };
